import { useMutation } from "@apollo/client"
import moment from "moment"
import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Col, Input, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import CustomButton from "../layout/CustomButton"
import { ADD_TEMP_BILLING_COST_RATE } from "../temp-billing-reconciliation/gql"

// Adds one People 2.0 cost-rate period. Append-only: once saved it cannot be
// edited or deleted, so a typo is corrected by a later period, never by
// rewriting this one — the confirmation copy says so before saving.
//
// Percentages are entered as plain numbers (5 means 5%), the way People 2.0's
// rate notices state them. The server re-validates every field and refuses a
// date before 1/1/2026 or one that already has a period; those refusals are
// shown inline, where the executive is looking.

interface IAddRatePeriodModalProps {
  isOpen: boolean;
  toggle: () => void;
  /** 'YYYY-MM-DD' — the first week ending that is calculated at all. */
  minDate: string;
  /** 'YYYY-MM-DD' of the newest existing period, '' when there is none. */
  latestEffectiveFrom: string;
  refetch: Function;
}

/** A percent field: blank, or a number from 0 to 100 inclusive. */
const pctError = (label: string, value: string): string => {
  if (value.trim() === '') return `${label} is required`
  const parsed = Number(value)
  if (!isFinite(parsed) || parsed < 0 || parsed > 100) return `${label} must be between 0 and 100`
  return ''
}

const AddRatePeriodModal: FC<IAddRatePeriodModalProps> = ({
  isOpen,
  toggle,
  minDate,
  latestEffectiveFrom,
  refetch,
}) => {
  const [effectiveFrom, setEffectiveFrom] = useState<string>('')
  const [feePct, setFeePct] = useState<string>('')
  const [burdenCaPct, setBurdenCaPct] = useState<string>('')
  const [burdenNonCaPct, setBurdenNonCaPct] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [addRate, { loading }] = useMutation(ADD_TEMP_BILLING_COST_RATE, {
    onCompleted: () => {
      toast.success("Rate period added")
      refetch()
      toggle()
    },
    // A duplicate date or a date before the cutoff arrives as a plain message —
    // show it in the modal, not only as a toast.
    onError: (error) => {
      setErrorMessage(error?.message || "something went wrong")
      toast.error(error?.message || "something went wrong")
    },
  })

  useEffect(() => {
    setEffectiveFrom('')
    setFeePct('')
    setBurdenCaPct('')
    setBurdenNonCaPct('')
    setNote('')
    setErrorMessage('')
  }, [isOpen])

  const fieldError =
    (effectiveFrom && effectiveFrom < minDate
      ? `Effective from must be on or after ${moment(minDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}`
      : '')
    || (feePct && pctError('People 2.0 fee %', feePct))
    || (burdenCaPct && pctError('Burden CA %', burdenCaPct))
    || (burdenNonCaPct && pctError('Burden non-CA %', burdenNonCaPct))
    || ''

  const complete =
    !!effectiveFrom && !!feePct.trim() && !!burdenCaPct.trim() && !!burdenNonCaPct.trim()

  // Not refused (the server allows it), but a period dated on or before the
  // newest one re-rates weeks an existing period already covers. Settled
  // invoices keep their frozen figure; open ones would move — so say so.
  const backdated = !!effectiveFrom && !!latestEffectiveFrom && effectiveFrom <= latestEffectiveFrom

  const onSave = async () => {
    if (!complete || fieldError) return
    setErrorMessage('')
    await addRate({
      variables: {
        input: {
          effectiveFrom,
          people20FeePct: Number(feePct),
          burdenCaPct: Number(burdenCaPct),
          burdenNonCaPct: Number(burdenNonCaPct),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
    })
  }

  const pctInput = (value: string, setter: (next: string) => void, placeholder: string) => (
    <Input
      type="number"
      step="0.01"
      min={0}
      max={100}
      value={value}
      placeholder={placeholder}
      onChange={({ target: { value: next } }) => {
        setErrorMessage('')
        setter(next)
      }}
    />
  )

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="revampDialog revampDialogWidth">
      <ModalHeader toggle={toggle}>Add Rate Period</ModalHeader>
      <ModalBody className="p-4">
        <Col>
          <p className="text-muted">
            Applies to weeks ending on or after the effective-from date, until the next period
            starts. A saved period cannot be edited or deleted — correct a mistake by adding a
            later period.
          </p>
          <Row>
            <Col sm="6">
              <p className="font-weight-bold">Effective from <span className="text-danger">*</span></p>
              <Input
                type="date"
                value={effectiveFrom}
                min={minDate}
                onChange={({ target: { value } }) => {
                  setErrorMessage('')
                  setEffectiveFrom(value)
                }}
              />
            </Col>
            <Col sm="6">
              <p className="font-weight-bold">People 2.0 fee % <span className="text-danger">*</span></p>
              {pctInput(feePct, setFeePct, '5.00')}
              <small className="text-muted">Charged on the full invoice amount, sales tax included.</small>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col sm="6">
              <p className="font-weight-bold">Burden CA % <span className="text-danger">*</span></p>
              {pctInput(burdenCaPct, setBurdenCaPct, '15.31')}
              <small className="text-muted">Employee burden on the gross wage when the temp lives in California.</small>
            </Col>
            <Col sm="6">
              <p className="font-weight-bold">Burden non-CA % <span className="text-danger">*</span></p>
              {pctInput(burdenNonCaPct, setBurdenNonCaPct, '14.09')}
              <small className="text-muted">Employee burden on the gross wage everywhere else.</small>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <p className="font-weight-bold">Note</p>
              <Input
                type="textarea"
                rows={2}
                maxLength={500}
                value={note}
                placeholder="e.g. People 2.0 rate notice dated 02/16/2026"
                onChange={({ target: { value } }) => setNote(value)}
              />
            </Col>
          </Row>
        </Col>

        {backdated && !fieldError && (
          <Col className="mt-3">
            <p className="text-warning m-0">
              This date is on or before the newest existing period
              ({moment(latestEffectiveFrom, 'YYYY-MM-DD').format('MM/DD/YYYY')}). It will change
              the rates for the weeks it now covers on invoices not yet settled.
            </p>
          </Col>
        )}

        {!!(fieldError || errorMessage) && (
          <Col className="mt-3">
            <p className="text-danger m-0">{fieldError || errorMessage}</p>
          </Col>
        )}

        <div className="mt-5 d-flex w-40 align-self ml-auto">
          <CustomButton
            className="ml-auto"
            disabled={loading || !complete || !!fieldError}
            loading={loading}
            buttonClick={onSave}
            buttonText="Add Period"
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AddRatePeriodModal
